import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Check, X, Clock, Play, Square, RotateCcw } from "lucide-react";
import { maskTime, pinCaretEnd } from "@/lib/timeMask";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function ItemStopwatch({
  item, idx, readOnly,
  editingTimeId, setEditingTimeId, customTime, setCustomTime,
  formatStopwatch, getElapsed,
  startTimer, stopTimer, resetTimer,
  updateItemStopwatch,
}) {
  const [confirmReset, setConfirmReset] = useState(false);

  const parseTime = (str) => {
    const parts = str.split(':').map(Number);
    if (parts.length === 3) return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
    if (parts.length === 2) return (parts[0] || 0) * 60 + (parts[1] || 0);
    return (parts[0] || 0) * 60;
  };

  const saveTime = () => {
    const secs = Math.min(parseTime(customTime), 60 * 3600);
    if (secs >= 0) updateItemStopwatch(item.id, { stopwatch_elapsed: secs, stopwatch_started_at: null });
    setEditingTimeId(null);
    setCustomTime("");
  };

  const cancelEdit = () => { setEditingTimeId(null); setCustomTime(""); };

  return (
    <div className="mt-3 pt-3 border-t-4 border-zinc-300 flex items-center gap-2" data-testid={`stopwatch-${idx}`}>
      <Clock className="w-3.5 h-3.5 text-zinc-400" />
      {!readOnly && (
      <span data-testid={`stopwatch-time-area-${idx}`} className="flex items-center gap-1">
      {editingTimeId === item.id ? (
        <div className="flex items-center gap-1">
          <Input
            type="text" value={customTime} onChange={(e) => setCustomTime(maskTime(e.target.value))}
            onSelect={pinCaretEnd} inputMode="numeric"
            className="w-24 h-8 text-sm font-mono font-bold border-zinc-300 rounded-none text-center"
            placeholder="00:00:00" autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') saveTime(); else if (e.key === 'Escape') cancelEdit(); }}
            data-testid={`custom-time-input-${idx}`}
          />
          <button onClick={saveTime} className="p-1.5 bg-zinc-700 text-white hover:bg-zinc-800 transition-colors rounded-sm" data-testid={`save-custom-time-${idx}`}>
            <Check className="w-3.5 h-3.5" />
          </button>
          <button onClick={cancelEdit} className="p-1.5 text-zinc-400 hover:text-zinc-600">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <span
          className="text-sm font-mono font-bold text-zinc-700 min-w-[52px] cursor-pointer hover:underline"
          onClick={() => { setEditingTimeId(item.id); setCustomTime(maskTime(formatStopwatch(getElapsed(item)))); }}
          data-testid={`stopwatch-display-${idx}`} title="Tap to enter custom time"
        >
          {formatStopwatch(getElapsed(item))}
        </span>
      )}
      </span>
      )}
      {readOnly && (
        <span className="text-sm font-mono font-bold text-zinc-700 min-w-[52px]">
          {formatStopwatch(getElapsed(item))}
        </span>
      )}
      {!readOnly && !item.checked && (
        !item.stopwatch_started_at ? (
          <button onClick={() => startTimer(item.id)} className="p-1.5 bg-green-600 text-white hover:bg-green-700 transition-colors rounded-sm" data-testid={`stopwatch-start-${idx}`} title="Start">
            <Play className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button onClick={() => stopTimer(item.id)} className="p-1.5 bg-red-600 text-white hover:bg-red-700 transition-colors rounded-sm" data-testid={`stopwatch-stop-${idx}`} title="Stop">
            <Square className="w-3.5 h-3.5" />
          </button>
        )
      )}
      {!readOnly && !item.checked && (
        <button onClick={() => setConfirmReset(true)} disabled={!!item.stopwatch_started_at}
          className="p-1.5 text-zinc-400 hover:text-zinc-600 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
          data-testid={`stopwatch-reset-${idx}`} title="Reset"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      )}
      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent className="rounded-none max-w-sm" data-testid={`stopwatch-reset-confirm-${idx}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset stopwatch?</AlertDialogTitle>
            <AlertDialogDescription>
              This sets the recorded time back to 00:00 and also clears the cut date on this component.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300" data-testid={`stopwatch-reset-cancel-${idx}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => resetTimer(item.id)} className="rounded-none bg-red-600 hover:bg-red-700" data-testid={`stopwatch-reset-confirm-btn-${idx}`}>
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
